import React from 'react';
import {useId} from '../hooks/useId';
import {PopoverContext} from './PopoverContext';
import type {PopoverContextProps} from './PopoverContext';

export interface PopoverProps {
    /*
      IDs are automatically generated by component for accessibility purposes.
      Use this prop to pass a specific ID value that will be appended: `popover-${id}`.
    */
    id?: string;
    /* The content of the component */
    children: React.ReactNode;
    /**
     * If true, this will show the close button. Popovers should
     * always have at least one focusable control, so if this is
     * set to false there should be another interactive element instead.
     * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/dialog_role
     */
    showClose?: boolean;
}

export const Popover =
  (
      {
          children,
          showClose = true,
          id
      }: PopoverProps
  ): React.ReactElement => {
      const [open, setOpen] = React.useState(false);
      const anchorRef = React.useRef<HTMLElement>(null);
      const popoverId = useId(id, 'popover');

      const handleToggle = (): void => {
          setOpen((prevOpen) => !prevOpen);
      };

      const handleClose = (event: Event | React.SyntheticEvent): void => {
          if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
              return;
          }

          setOpen(false);
      };

      const handleKeyDown = (event: React.KeyboardEvent): void => {
          if (event.key === 'Escape') {
              event.preventDefault();
              setOpen(false);
          }
      };

      const contextValues: PopoverContextProps = {
          open,
          handleToggle,
          handleClose,
          handleKeyDown,
          popoverId,
          anchorRef,
          showClose
      };

      return (
          <PopoverContext.Provider value={contextValues}>
              {children}
          </PopoverContext.Provider>
      );
  };

Popover.displayName = 'Popover';
